<template>
    <component
        :is="to ? 'router-link' : href ? 'a' : 'button'"
        v-bind="rootAttrs"
        :class="[
            'inline-flex items-center justify-center gap-2 font-medium rounded-md transition-all duration-150 flex-shrink-0 cursor-pointer',
            fullWidth ? 'w-full' : '',
            iconOnly ? iconSizeClasses : sizeClasses,
            styleClasses,
            disabled ? disabledClasses : processingClasses,
            !disabled && !processing && hoverClasses,
        ]"
        @click="handleClick"
    >
        <!-- Icon (left side) -->
        <slot v-if="!processing" name="icon"></slot>

        <!-- Content or Processing State -->
        <div
            v-if="!processing"
            :class="iconOnly ? 'flex items-center' : 'flex items-center gap-2'"
        >
            <slot></slot>
        </div>
        <div v-if="processing" class="flex items-center gap-2">
            <svg
                class="animate-spin h-4 w-4"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                ></circle>
                <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
            </svg>
            <span v-if="!iconOnly">{{ processingLabel }}</span>
        </div>
    </component>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
    type: {
        type: String,
        default: 'button',
        validator: (value) => ['button', 'submit', 'reset'].includes(value),
    },
    variant: {
        type: String,
        default: 'primary',
        validator: (value) =>
            [
                'primary',
                'primary-danger',
                'secondary',
                'tertiary',
                'link',
                'outline-primary',
                'outline-neutral',
                'outline-danger',
                'outline-warning',
                'outline-success',
                'warning',
                'link-danger',
                'link-muted',
            ].includes(value),
    },
    size: {
        type: String,
        default: 'medium',
        validator: (value) =>
            ['small', 'medium', 'large', 'field'].includes(value),
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    processing: {
        type: Boolean,
        default: false,
    },
    processingLabel: {
        type: String,
        default: 'Please Wait',
    },
    fullWidth: {
        type: Boolean,
        default: false,
    },
    to: {
        type: [String, Object],
        default: null,
    },
    href: {
        type: String,
        default: null,
    },
    iconOnly: {
        type: Boolean,
        default: false,
    },
    ariaLabel: {
        type: String,
        default: null,
        validator: (value, props) => !props.iconOnly || !!value,
    },
})

const emit = defineEmits(['click'])

const handleClick = (event) => {
    if (props.disabled || props.processing) {
        event.preventDefault()
        return
    }
    emit('click', event)
}

// Declaring `ariaLabel` as a prop means Vue matches any `aria-label`
// attribute a caller passes (camelCase-normalized) to it and removes it
// from $attrs entirely — it never reaches native fallthrough, icon-only
// or not. So it must always be re-emitted here when present; omitted
// entirely (rather than set to undefined/null) when absent, so a plain
// text button with no aria-label falls back to its visible text content
// as the accessible name, same as before this prop existed.
const rootAttrs = computed(() => {
    const isLink = !!(props.to || props.href)
    return {
        ...(props.to ? { to: props.to } : {}),
        ...(!props.to && props.href ? { href: props.href } : {}),
        ...(!props.to && !props.href
            ? { type: props.type, disabled: props.disabled || props.processing }
            : {}),
        ...(props.ariaLabel ? { 'aria-label': props.ariaLabel } : {}),
        ...(isLink
            ? {
                  'aria-disabled': props.disabled || props.processing,
                  tabindex: props.disabled || props.processing ? -1 : undefined,
              }
            : {}),
    }
})

// Size classes - fixed dimensions to prevent layout shift.
// `field` is the height of a form control (text-sm line + py-2 + 1px border =
// 38px), for a button that sits in a row beside inputs and selects.
const sizeClasses = computed(() => {
    switch (props.size) {
        case 'small':
            return 'px-3 py-1.5 text-xs h-8 min-w-8'
        case 'large':
            return 'px-4 py-3 text-base h-11 min-w-11'
        case 'field':
            return 'px-3.5 py-2 text-sm h-9.5 min-w-9.5'
        case 'medium':
        default:
            return 'px-3.5 py-2 text-sm h-9 min-w-9'
    }
})

// Icon-only size classes - square padding, no min-w
const iconSizeClasses = computed(() => {
    switch (props.size) {
        case 'small':
            return 'p-1.5 h-8 w-8'
        case 'large':
            return 'p-3 h-11 w-11'
        case 'field':
            return 'p-2 h-9.5 w-9.5'
        case 'medium':
        default:
            return 'p-2 h-9 w-9'
    }
})

// Base style classes
// `branded-split-dark:` only applies on the pre-login branded screens in dark
// mode (see src/index.css); the signed-in app has no dark styling yet.
const baseStyleClasses = {
    primary:
        'bg-lime-600 text-white shadow-sm branded-split-dark:bg-lime-500 branded-split-dark:text-lime-950',
    'primary-danger': 'bg-red-600 text-white shadow-sm',
    secondary:
        'bg-white text-taupe-900 ring-1 ring-inset ring-taupe-300 shadow-sm',
    tertiary: 'bg-taupe-100 text-taupe-900',
    link: 'bg-transparent text-sky-600',
    'outline-primary': 'bg-white ring-1 ring-sky-300 text-sky-700',
    'outline-neutral': 'bg-white ring-1 ring-taupe-300 text-taupe-500',
    'outline-danger': 'bg-white ring-1 ring-red-300 text-red-600',
    'outline-warning': 'bg-white ring-1 ring-amber-300 text-amber-700',
    'outline-success': 'bg-white ring-1 ring-lime-300 text-lime-700',
    warning: 'bg-amber-600 text-white',
    'link-danger': 'bg-transparent text-red-600',
    'link-muted': 'bg-transparent text-taupe-500',
}

// Hover classes
const hoverStyleClasses = {
    primary: 'hover:bg-lime-700 branded-split-dark:hover:bg-lime-400',
    'primary-danger': 'hover:bg-red-700',
    secondary: 'hover:bg-taupe-50',
    tertiary: 'hover:bg-taupe-200',
    link: 'hover:text-sky-700 hover:underline',
    'outline-primary': 'hover:bg-sky-50',
    'outline-neutral':
        'hover:bg-lime-50 hover:ring-lime-300 hover:text-lime-700',
    'outline-danger': 'hover:bg-red-50',
    'outline-warning': 'hover:bg-amber-50',
    'outline-success': 'hover:bg-lime-50',
    warning: 'hover:bg-amber-700',
    'link-danger': 'hover:text-red-700',
    'link-muted': 'hover:text-taupe-700',
}

// Focus classes
const focusClasses = {
    primary:
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600 branded-split-dark:focus-visible:outline-lime-400',
    'primary-danger':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600',
    secondary:
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-taupe-300',
    tertiary:
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-taupe-400',
    link: 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600',
    'outline-primary':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600',
    'outline-neutral':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600',
    'outline-danger':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600',
    'outline-warning':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600',
    'outline-success':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lime-600',
    warning:
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-600',
    'link-danger':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600',
    'link-muted':
        'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-taupe-400',
}

const styleClasses = computed(() => {
    return `${baseStyleClasses[props.variant]} ${focusClasses[props.variant]}`
})

const hoverClasses = computed(() => {
    return hoverStyleClasses[props.variant]
})

const disabledClasses = computed(() => {
    return 'opacity-50 cursor-not-allowed'
})

const processingClasses = computed(() => {
    if (props.processing) {
        return 'opacity-75 cursor-not-allowed'
    }
    return ''
})
</script>
